import React, { forwardRef, memo, useImperativeHandle } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import Layout from 'antd/es/layout';
import Drawer from 'antd/es/drawer';

import { TabValues } from 'routes/RoutesNames';
import { useAppSelector } from 'redux/store';
import { useIsMobile } from 'hooks/useIsMobile';
import useCanCreateContract from 'hooks/useCanCreateContract';

import { Footer } from './components/Footer';
import { Header } from './components/Header';
import SubMenu from './components/SubMenu';
import { CustomTooltip } from './components/CustomTooltip';
import { useTabList } from './components/TabList';

export type TSideBarHandle = {
  isSideBarExist: () => boolean;
};

type TSideBarProps = {
  sideBarOpen: boolean;
  onClose: () => void;
  onLogout: () => void;
};

type TTabItem = {
  label: string;
  route: string;
  icon: React.ReactNode;
  dataCy?: string;
  isButton?: boolean;
  onClick?: () => void;
  subRoutes?: { label: string; route: string }[];
};

const SIDER_WIDTH = 260;
const RESIDENT_PRESELECTED = 'residents?states=RESIDENT%2CNOTICE%2CAPPLICANT%2CFUTURE_RESIDENT';

const handlePreselectedFilter = (route: string): string => {
  switch (route) {
    case 'residents':
      return RESIDENT_PRESELECTED;
    default:
      return route;
  }
};

const SideBar = forwardRef<TSideBarHandle, TSideBarProps>(({ sideBarOpen, onClose, onLogout }, ref) => {
  const { users, userType, impersonateOn } = useAppSelector((state) => state.usersSlice);
  const user = userType ? users?.[userType] : null;
  const { pathname } = useLocation();
  const isMobile = useIsMobile(1024);
  const canCreateContract = useCanCreateContract();

  const tabValue = TabValues[`/${pathname.split('/')[1]}`];
  const tabListData = useTabList({ impersonate: !!impersonateOn, profile: user });

  useImperativeHandle(ref, () => ({
    isSideBarExist: () => !(tabValue === 'intro' || isMobile),
  }));

  if (tabValue === 'intro' || !userType) {
    return null;
  }

  const tabs = (tabListData[userType] ?? []) as TTabItem[];

  const renderMenuItem = ({ label, icon, route, isButton, onClick, subRoutes, dataCy }: TTabItem) => {
    const isTabSelected = pathname.includes(route) || !!subRoutes?.some((item) => pathname.includes(item.route));
    const isDisabled = !canCreateContract && route === 'contracts';
    const targetPath = handlePreselectedFilter(route);
    const to = targetPath[0] === '/' ? targetPath : `/${targetPath}`;

    const itemInner = (
      <SItemInner $active={isTabSelected}>
        <SIcon $active={isTabSelected}>{icon}</SIcon>
        <SLabel>{label}</SLabel>
      </SItemInner>
    );

    return (
      <SMenuItemWrapper key={route} $disabled={isDisabled}>
        <SItemRow $active={isTabSelected}>
          <CustomTooltip route={route}>
            {isButton ? (
              <SButtonItem data-cy={dataCy} onClick={onClick} $active={isTabSelected} disabled={isDisabled}>
                {itemInner}
              </SButtonItem>
            ) : (
              <SLinkItem to={to} data-cy={dataCy} $active={isTabSelected}>
                {itemInner}
              </SLinkItem>
            )}
          </CustomTooltip>
        </SItemRow>
        {isTabSelected && subRoutes && subRoutes.length > 0 && <SubMenu routes={subRoutes} />}
      </SMenuItemWrapper>
    );
  };

  const navContent = (
    <>
      <Header onClose={onClose} isMobile={isMobile} />
      <SMenuList>{tabs.map(renderMenuItem)}</SMenuList>
      {isMobile && <Footer onClose={onClose} onLogout={onLogout} />}
    </>
  );

  if (isMobile) {
    return (
      <SDrawer
        placement='left'
        visible={sideBarOpen}
        onClose={onClose}
        width='100%'
        closable={false}
        bodyStyle={{ padding: 0 }}
      >
        {navContent}
      </SDrawer>
    );
  }

  return <SSider width={SIDER_WIDTH}>{navContent}</SSider>;
});

SideBar.displayName = 'SideBar';

export default memo(SideBar);

const SSider = styled(Layout.Sider)`
  &.ant-layout-sider {
    background: #ffffff;
    height: 100vh;
    position: sticky;
    top: 0;
    left: 0;
    z-index: 99;
    overflow: hidden;
    flex-shrink: 0;

    .ant-layout-sider-children {
      display: flex;
      flex-direction: column;
      height: 100%;
      overflow: hidden;
    }
  }
`;

const SDrawer = styled(Drawer)`
  .ant-drawer-content-wrapper {
    max-width: 572px;
  }
  .ant-drawer-body {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: #ffffff;
    padding: 0;
  }
`;

const SMenuList = styled.nav`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-top: 8px;

  @media (max-width: 1024px) {
    padding-top: 16px;
  }
`;

const SLabel = styled.span`
  font-family: 'aesthet-nova';
  font-weight: 500;
  font-size: 16px;
  white-space: nowrap;
`;

const SIcon = styled.span<{ $active: boolean }>`
  display: inline-flex;
  margin-right: 16px;

  & svg path {
    fill: ${({ $active }) => ($active ? '#ffffff' : '#fcc425')};
    transition: fill 0.2s;
  }
`;

const SItemInner = styled.span<{ $active: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: ${({ $active }) => ($active ? '200px' : '219px')};
  // margin-left: auto;
  margin-right: ${({ $active }) => ($active ? '16px' : '0')};
  padding: 16px 30px 16px 15px;
  border-radius: 4px;
  box-sizing: border-box;
  ${({ $active }) =>
    $active &&
    css`
      background-color: #0d0d0d;
      color: #ffffff;
      @media (max-width: 1024px) {
        border-radius: 8px;
      }
    `}

  @media (max-width: 1024px) {
    width: ${({ $active }) => ($active ? 'calc(100% - 32px)' : '100%')};
    margin-right: ${({ $active }) => ($active ? '16px' : '0')};
  }
`;

const SMenuItemWrapper = styled.div<{ $disabled: boolean }>`
  margin-bottom: 4px;

  ${({ $disabled }) =>
    $disabled &&
    css`
      pointer-events: none;
      opacity: 0.5;
    `}
`;

const SItemRow = styled.div<{ $active: boolean }>`
  position: relative;

  ${({ $active }) =>
    $active &&
    css`
      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 4px;
        height: 60px;
        background: #fcc425;
        z-index: 1;

        @media (max-width: 1024px) {
          display: none;
        }
      }
    `}
`;

const itemBaseStyles = css`
  display: block;
  width: 219px;
  margin-left: auto;
  cursor: pointer;
  text-decoration: none;
  color: #0d0d0d;

  @media (max-width: 1024px) {
    width: calc(100% - 48px);
    margin: 0 24px;
  }
`;

const SLinkItem = styled(Link)<{ $active: boolean }>`
  ${itemBaseStyles}
`;

const SButtonItem = styled.button<{ $active: boolean }>`
  ${itemBaseStyles}
  border: none;
  background: transparent;
  font: inherit;
  text-align: left;
  padding: 0;
`;
